import { useTranslations } from 'use-intl'

import { cn } from '@/lib/utils'
import { trackFaqOpen } from '@/lib/analytics/events'
import { BookDemoButton } from '@/components/cta-buttons'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Reveal, RevealGroup, RevealItem } from '@/components/ui/reveal'

// ponytail: no data module — each page owns its question list in its own
// messages namespace and passes the keys; the defaults are the landing's.
export const DEFAULT_FAQ_ITEMS = [
  'what',
  'trial',
  'usability',
  'emergency',
  'register',
] as const

/**
 * Unlike the neighbouring bands, no fixed height (`--landing-band-h`) and no
 * background: the content dictates the height.
 * `namespace` must hold `titleLead`/`titleTail`/`subtitle` plus
 * `items.<key>.question`/`.answer` for every key in `items`.
 */
export function FaqSection({
  namespace = 'home.faq',
  items = DEFAULT_FAQ_ITEMS,
  className,
}: {
  namespace?: string
  items?: readonly string[]
  className?: string
}) {
  const t = useTranslations(namespace)

  return (
    <section className={cn('py-12 md:py-20 lg:py-28', className)}>
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 md:gap-12 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <RevealGroup className="max-w-md lg:self-center">
          {/* Two-line heading, as in the mock. The break comes from two keys +
              `block` (the ExpertSection idiom) rather than a <br>, so each
              locale picks its own break point. */}
          <RevealItem>
            <h2 className="font-serif text-4xl font-semibold tracking-tight text-accent-foreground sm:text-5xl">
              <span className="block">{t('titleLead')}</span>
              <span className="block">{t('titleTail')}</span>
            </h2>
          </RevealItem>
          <RevealItem className="mt-4">
            <p className="font-mono text-sm text-accent-foreground/75">
              {t('subtitle')}
            </p>
          </RevealItem>
          <RevealItem className="mt-8">
            <BookDemoButton source="faq" size="lg" className="h-12 px-8 sm:h-11 lg:text-base" />
          </RevealItem>
        </RevealGroup>

        {/* ponytail: one Reveal over the whole accordion, no per-item cascade —
            six lines of text cascading is exactly the "too much" we're avoiding. */}
        <Reveal>
          {/* `type="single" collapsible`: one answer open at a time, the first
              one open on load as in the mock. Items are separate cards, not a
              stack: `last:border-b` cancels shadcn's own `last:border-b-0`,
              which would otherwise strip the last card of its bottom edge. */}
          <Accordion
            type="single"
            collapsible
            defaultValue={items[0]}
            onValueChange={(value) => {
              if (value) trackFaqOpen({ question: value })
            }}
            className="flex flex-col gap-3"
          >
            {items.map((key) => (
              <AccordionItem
                key={key}
                value={key}
                className="rounded-xl border border-primary/20 bg-card px-2 last:border-b"
              >
                <AccordionTrigger className="text-base font-semibold text-accent-foreground">
                  {t(`items.${key}.question`)}
                </AccordionTrigger>
                {/* `px-4` aligns the answer with the question: the shadcn
                    trigger carries its own `px-4`, which the content does not
                    inherit. `pr-8` keeps the answer clear of the chevron gutter. */}
                {/* `t.rich` so answers can carry emphasis and line breaks —
                    plain-text answers (the service-page FAQs) pass through
                    unchanged since they contain no tags. */}
                <AccordionContent className="px-4 pr-8 text-sm text-accent-foreground/75">
                  {t.rich(`items.${key}.answer`, {
                    b: (chunks) => (
                      <b className="font-semibold text-accent-foreground">{chunks}</b>
                    ),
                    br: () => <br />,
                  })}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  )
}
